import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Redirect, useHistory, useLocation } from 'react-router';
import { useSnackbar } from 'notistack';
import { CognitoStatus } from 'helpers/enums';


interface IUsersContext {
    user?: IUser
    isSearchDone: boolean
    isLoading: boolean
    emailSearch: string
    deleteRequest: IDeleteRequest

    doSearch: () => void
    updateSearch: (email: string) => void
    deleteUser: () => Promise<boolean>
    updateDeleteRequest: (request: IDeleteRequest) => void
}

interface IUser {
    email: string
    firstName: string
    lastName: string
    countryCode: string
    name: string
    cognitoCreatedOn: moment.Moment
    warrantyCreatedOn: moment.Moment
    canDeleteUser: boolean
    foundInWarranty: boolean
    cognitoStatus: CognitoStatus
    providers?: string
}

export interface IDeleteRequest {
    email: string
    deleteUserRegistrations: boolean
    deleteWarrantyProfile: boolean
    deleteCognitoUsers: boolean
}

export let usersContext = React.createContext({} as IUsersContext);

let { Provider } = usersContext;

let UsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let location = useLocation();
    let history = useHistory();
    let { enqueueSnackbar } = useSnackbar();

    let [isLoading, setLoading] = useState(false);
    let [user, setUser] = useState<IUser>();

    let [isSearchDone, setSearchDone] = useState(false);
    let [emailSearch, setEmailSearch] = useState('');

    let [deleteRequest, setDeleteRequest] = useState<IDeleteRequest>({
        email: '',
        deleteCognitoUsers: true,
        deleteUserRegistrations: true,
        deleteWarrantyProfile: true,
    });


    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { emailSearch } }} />


    let processState = (state: any) => {
        if (state.emailSearch && state.emailSearch.trim() !== '') {
            setEmailSearch(state.emailSearch);

            searchUser(state.emailSearch);
        }
    }

    let doSearch = () => {
        history.push(location.pathname, { emailSearch });
    }

    let searchUser = async (email: string) => {
        let regExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        if (email === '' || !email.toLowerCase().match(regExp)) {
            enqueueSnackbar("Please specify valid email", { variant: 'error' });
            return;
        }

        try {
            setLoading(true);

            let res = await axios.get('/api/UserManagement/SearchUser', { params: { email } })
            setUser(res.data.item);

        } catch (err) { }
        finally {
            setSearchDone(true);
            setLoading(false);
        }
    }

    let updateDeleteRequest = (request: IDeleteRequest) => {
        setDeleteRequest(request);
    }

    let deleteUser = async () => {
        if (window.confirm("Are you sure that you want to remove this user?") === false)
            return;

        try {
            let res = await axios.delete(`/api/UserManagement/DeleteUser`, { params: deleteRequest });

            if (res.data.hasDeleted) {
                setSearchDone(false);
                setUser(undefined);
            }

            return res.data.hasDeleted;

        } catch (err) {
            return false;
        }
    }

    let updateSearch = (email: string) => {
        setEmailSearch(email);
    }


    return (
        <Provider
            value={{
                user,
                isLoading,
                isSearchDone,
                emailSearch,
                deleteRequest,

                updateSearch,
                doSearch,
                deleteUser,
                updateDeleteRequest
            }}
        >
            {children}
        </Provider>
    )
}

export default UsersProvider;